import {
  type ShoppingStore,
  type ServingAddressDetailsResponse,
} from '@/features/shop/services/ShoppingStore/types'
import { apiGet } from '@/features/shared/utils/dataFetching'
import { normalizeStoresResponse } from '@/features/shop/services/ShoppingStore/utils'
import { type QueryFunctionContext } from '@tanstack/react-query'
import {
  type ShoppingStoresKey,
  type ShoppingStoreByIdQueryKey,
} from '@/features/shop/services/ShoppingStore/queries'
import { isOnServer } from '@shared/constants/util'
import { getGuestStoreParamsSSR } from '@/features/shared/utils/dataFetching/guestStoreParams'
import { guestStoreCookie } from '@/features/account/services/GuestUser/cookies'

export const fetchShoppingStores = async (
  context: QueryFunctionContext<ShoppingStoresKey>,
  { preferredStoreLocationId }: { preferredStoreLocationId?: number } = {},
) => {
  const [, params] = context.queryKey
  const contextMeta = context.meta
  // The store location returned from Metropolis may differ from the one provided by the PLA region.
  // Therefore, it is important to pass a preferred store location ID provided by PLA to Metropolis to
  // attempt to retrieve the preferred store we want a guest user to see. Currently, the preferredStoreLocationId
  // parameter is utilized exclusively for the PLA flow involving visitor users. We will also include the preferred
  // store location ID in the request whenever a store location ID is available from the guest store cookie in order
  // to ensure that the returned store data from Metropolis is the store location they have selected.
  const getPreferredStoreLocationId = () => {
    if (preferredStoreLocationId) return preferredStoreLocationId

    if (isOnServer()) {
      if (contextMeta?.cookies) {
        return getGuestStoreParamsSSR(contextMeta.cookies).guestStore?.store_location_id
      }
    }
    return guestStoreCookie.get()?.store_location_id
  }
  const response = await apiGet<ServingAddressDetailsResponse>({
    config: {
      url: 'api/v1/store_locations/serving_address_with_details',
      params: {
        ...params,
        preferred_store_location_ids: getPreferredStoreLocationId(),
      },
    },
    context,
    options: {
      ignoredMessages: [
        'Address not in service area',
        'invalid address lines',
        'Missing or invalid user', // auth token expired, user is refreshing/logging out
      ],
    },
    fetcherName: 'fetchShoppingStores',
  })
  return normalizeStoresResponse(response)
}

export const fetchShoppingStoreById = (
  context: QueryFunctionContext<ShoppingStoreByIdQueryKey>,
) => {
  const [, { storeId }] = context.queryKey
  return apiGet<ShoppingStore>({
    config: { url: `api/v1/stores/${storeId}.json` },
    context,
    fetcherName: 'fetchShoppingStoreById',
  })
}
